<script setup lang="ts">
import { defineProps, ref } from "vue";
import Payment from "../Payment.vue";
import { onMounted } from "vue";
import { useNotification } from "@kyvg/vue3-notification";
import { useI18n } from "vue-i18n";
import axios from "axios";

const notification = useNotification()
const { t } = useI18n()

const { clientSecret, paymentCallbackUrl, amount } = defineProps({
  clientSecret: {
    type: String,
    required: true,
  },
  paymentCallbackUrl: {
    type: String,
    required: true,
  },
  amount: {
    type: Number,
    required: true,
  }
})
const disablePayment = ref(false);

// Déclaration pour étendre l'interface Window
declare global {
  interface Window {
    STRIPE_PUBLIC_KEY?: string;
    Stripe?: any;
  }
}

const stripe = ref<any>(null);
onMounted(() => {
  if (window.STRIPE_PUBLIC_KEY && window.Stripe) {
    stripe.value = window.Stripe(window.STRIPE_PUBLIC_KEY);
  }
})

const deposit = ref(null);

const disableSubmitButton = ref(false);
const status = ref('');

const paymentRef = ref<InstanceType<typeof Payment> | null>(null);
const onDepositSubmit = async () => {
  if (!paymentRef.value) {
    return;
  }
  try {
    disableSubmitButton.value = true;
    status.value = t('Processing deposit…');
    const paymentIntent = await paymentRef.value.submitPayment();
    handlePaymentIntent(paymentIntent);
  } catch (e) {
    console.error(e);
    status.value = '';
    disableSubmitButton.value = false;
  }
}

const handlePaymentIntent = async (paymentIntent) => {
  if (paymentIntent.status === 'requires_capture') {
    // Show a success message to your customer
    notification.notify({
      group: 'success',
      type: 'success',
      title: 'Payment successful',
    });

    try {
      status.value = t('Saving deposit…');
      await axios.post(paymentCallbackUrl, {
        stripe : paymentIntent
      })
      success.value = true;
    } catch (e) {
      console.error(e)
      status.value = '';
      disableSubmitButton.value = false;
    }
  }
}

const success = ref(false);

</script>

<template>
    <div class="row justify-content-center">
        <div class="col col-lg-6" v-if="!success">
            <h3>
              {{ $t('Payment of the Security Deposit') }}
            </h3>
            <p>
              {{ $t('Welcome to the Security Deposit page! We invite you to provide a deposit to validate your registration. Rest assured, this is simply a bank authorization.') }}
              <strong>{{ $t('No amount will be debited from your account.') }}</strong>
            </p>
            <p>
              {{ $t('This procedure allows us to verify that the funds are available.') }}
              {{ $t('At the end of the tests, the authorization will be automatically canceled, and the funds will be immediately available in your account again.') }}
            </p>

            <form action="" @submit.prevent="onDepositSubmit" class="mt-4">
              <Payment :stripe="stripe"
                       :client-secret="clientSecret"
                       :disabled="disablePayment"
                       ref="paymentRef"
                       @cardUpdated="deposit = $event"></Payment>
              <div class="form-group">
                <button type="submit" class="btn btn-primary btn-block p-2" :disabled="disableSubmitButton">
                  {{ status != '' ? status : $t("Confirm and authorize deposit", { amount }) }}
                </button>
              </div>
            </form>
        </div>
        <div class="col-lg-6" v-else>
            <h3>{{ $t('Authorization Confirmation') }}</h3>
            <h4>{{ $t('Your security deposit has been successfully authorized!')}}</h4>
            <p>{{ $t("Thank you for providing the necessary information. We have confirmed the authorization on your bank account.", { amount }) }}</p>
            <p>{{ $t('Remember, no amount will be debited from your account at this stage.') }}</p>
            <p>{{ $t('At the end of the tests, the authorization will be automatically canceled, and the funds will be immediately available in your account again.') }}</p>
        </div>
    </div>
</template>

<style scoped>

</style>